'use client';

import { useUserEventLogger } from '@repo/tracking-service';
import { BarTabs, Chip } from '@repo/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { getContentCategory } from '@/lib/api/contents';
import { CONTENT_CATEGORY_EXCLUDED_NAMES } from '@/lib/constants/platform';
import { ContentFilter } from '@/lib/types/content';

interface ContentCategoryProps {
  onCategoryFilterChange?: (categoryFilter?: string) => void;
  onHasSecondDepthChange?: (hasSecondDepth: boolean) => void;
}

/**
 * API 응답이 배열이거나 { categories | subcategories } 형태일 수 있어
 * 이를 공통으로 파싱하기 위한 유틸 함수
 */
const parseFilterList = (data: any, field: 'categories' | 'subcategories'): ContentFilter[] => {
  return (Array.isArray(data) ? data : data?.[field] || []) as ContentFilter[];
};

/**
 * 카테고리 목록 앞에 '전체' 항목을 추가
 */
const prependAll = (filters: ContentFilter[]): ContentFilter[] => [{ key: undefined, name: '전체', order: 0 }, ...filters];

const removeSpaces = (str: string): string => str.replace(/\s+/g, '');

const excludeCategories = (filters: ContentFilter[]): ContentFilter[] => {
  const excludedSet = new Set(CONTENT_CATEGORY_EXCLUDED_NAMES.map((name) => removeSpaces(name)));
  return filters.filter((filter) => !excludedSet.has(removeSpaces(filter.name)));
};

export const ContentCategory = ({ onCategoryFilterChange, onHasSecondDepthChange }: ContentCategoryProps) => {
  const { sendClickEvent } = useUserEventLogger();
  const [firstDepthFilters, setFirstDepthFilters] = useState<ContentFilter[]>([]);
  const [secondDepthFilters, setSecondDepthFilters] = useState<ContentFilter[]>([]);

  const [selectedFirstDepthKey, setSelectedFirstDepthKey] = useState<string | undefined>(undefined);
  const [selectedSecondDepthKey, setSelectedSecondDepthKey] = useState<string | undefined>(undefined);

  const [isLoading, setIsLoading] = useState(true);

  const tabLabels = useMemo(() => firstDepthFilters.map((filter) => filter.name), [firstDepthFilters]);

  const selectedFirstDepthIndex = useMemo(() => {
    if (firstDepthFilters.length === 0) return 0;
    const idx = firstDepthFilters.findIndex((f) => f.key === selectedFirstDepthKey);
    return idx >= 0 ? idx : 0;
  }, [firstDepthFilters, selectedFirstDepthKey]);

  const fetchFilters = useCallback(async (parentKey?: string, signal?: AbortSignal) => {
    const response = await getContentCategory(parentKey, signal);
    if (!response.ok) {
      throw new Error('Failed to fetch categories');
    }
    return response.json();
  }, []);

  /**
   * 선택된 카테고리 상태를 API filter 파라미터 문자열로 변환
   *
   * @param firstKey - 선택된 1뎁스 카테고리 key (undefined면 '전체')
   * @param secondKey - 선택된 2뎁스 카테고리 key (undefined면 '전체')
   *
   * 전달되는 필터 값:
   * - 1뎁스가 '전체'인 경우: undefined
   * - 1뎁스만 선택된 경우(2뎁스 '전체'): "1뎁스키"
   * - 2뎁스 특정 선택: "1뎁스키.2뎁스키"
   */
  const getCategoryFilterParam = useCallback(
    (firstKey?: string, secondKey?: string) => {
      if (!firstKey) {
        onCategoryFilterChange?.(undefined);
      } else if (secondKey) {
        onCategoryFilterChange?.(`${firstKey}.${secondKey}`);
      } else {
        onCategoryFilterChange?.(firstKey);
      }
    },
    [onCategoryFilterChange]
  );

  /**
   * 1뎁스 카테고리 최초 로딩
   */
  useEffect(() => {
    const abortController = new AbortController();

    (async () => {
      try {
        setIsLoading(true);

        const data = await fetchFilters(undefined, abortController.signal);
        const filters = excludeCategories(prependAll(parseFilterList(data, 'categories')));
        setFirstDepthFilters(filters);

        const initialFirstKey = filters[0]?.key;
        setSelectedFirstDepthKey(initialFirstKey);
        setSelectedSecondDepthKey(undefined);

        getCategoryFilterParam(initialFirstKey, undefined);
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        console.error('Failed to fetch first depth categories:', error);
        setFirstDepthFilters([]);
      } finally {
        setIsLoading(false);
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [fetchFilters, getCategoryFilterParam]);

  /**
   * 선택된 1뎁스에 따라 2뎁스 카테고리 조회
   * - 1뎁스가 '전체'면 2뎁스는 비움
   */
  useEffect(() => {
    const abortController = new AbortController();

    const resetSecondDepth = () => {
      setSecondDepthFilters([]);
      setSelectedSecondDepthKey(undefined);
      onHasSecondDepthChange?.(false);
    };

    if (firstDepthFilters.length === 0) {
      resetSecondDepth();
      return;
    }

    if (!selectedFirstDepthKey) {
      resetSecondDepth();
      getCategoryFilterParam(undefined, undefined);
      return;
    }

    (async () => {
      try {
        const data = await fetchFilters(selectedFirstDepthKey, abortController.signal);
        const filters = prependAll(parseFilterList(data, 'subcategories'));
        setSecondDepthFilters(filters);
        setSelectedSecondDepthKey(undefined);
        onHasSecondDepthChange?.(filters.length > 0);

        getCategoryFilterParam(selectedFirstDepthKey, undefined);
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        console.error('Failed to fetch second depth categories:', error);
        resetSecondDepth();
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [firstDepthFilters.length, selectedFirstDepthKey, fetchFilters, getCategoryFilterParam, onHasSecondDepthChange]);

  const handleFirstDepthSelect = useCallback(
    (index: number) => {
      const selectedFilter = firstDepthFilters[index];
      const nextFirstKey = selectedFilter?.key;
      const categoryName = selectedFilter?.name || '전체';

      sendClickEvent('category-major', { id: removeSpaces(categoryName) });

      setSelectedFirstDepthKey(nextFirstKey);
      setSelectedSecondDepthKey(undefined);

      getCategoryFilterParam(nextFirstKey, undefined);
    },
    [firstDepthFilters, getCategoryFilterParam, sendClickEvent]
  );

  const handleSecondDepthSelect = useCallback(
    (clickedKey?: string) => {
      const nextKey = selectedSecondDepthKey === clickedKey ? undefined : clickedKey;
      const toggleState = nextKey ? 'on' : 'off';

      const firstDepthFilter = firstDepthFilters.find((f) => f.key === selectedFirstDepthKey);
      const depth1Name = firstDepthFilter?.name || '전체';

      const secondDepthFilter = secondDepthFilters.find((f) => f.key === clickedKey);
      const depth2Name = secondDepthFilter?.name || '전체';

      sendClickEvent('category-medium', {
        depth1: removeSpaces(depth1Name),
        depth2: removeSpaces(depth2Name),
        toggle_state: toggleState
      });

      setSelectedSecondDepthKey(nextKey);
      getCategoryFilterParam(selectedFirstDepthKey, nextKey);
    },
    [selectedFirstDepthKey, selectedSecondDepthKey, firstDepthFilters, secondDepthFilters, getCategoryFilterParam, sendClickEvent]
  );

  if (isLoading || firstDepthFilters.length === 0) return null;

  return (
    <div className="sticky top-[52px] z-20 bg-neutral-background-main w-full">
      <div className="-mx-5 mt-normal-small mb-normal-large [&_p]:!my-0 [&_hr]:!my-0">
        <div className="overflow-x-auto">
          <BarTabs className="[&>div>div]:gap-tiny-large" tabsItem={tabLabels} selectedIndex={selectedFirstDepthIndex} selectTab={handleFirstDepthSelect} />
        </div>
        {secondDepthFilters.length > 0 && (
          <div
            className="overflow-x-auto max-w-full py-normal-small scrollbar-hide"
            style={{
              maskImage: 'linear-gradient(90deg, #0000, #000 20px, #000 calc(100% - 20px), #0000)'
            }}
          >
            <div className="flex gap-tiny-large whitespace-nowrap w-fit container-padding-medium">
              {secondDepthFilters.map((filter) => (
                <Chip
                  key={filter.key || 'all'}
                  label={filter.name}
                  isSelected={selectedSecondDepthKey === filter.key}
                  onClick={() => handleSecondDepthSelect(filter.key)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
